import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { LaravelAuthService, LaravelUser } from './laravelAuth.service';
import { PermissionsService } from './permissions.service';
import { UserSettingsService } from './userSettings.service';

import { User } from '../../ressources/types/user';

import { HttpClient, HttpHeaders } from '@angular/common/http';

import { Globals } from '../../ressources/variables/globals';

@Injectable({
  providedIn: 'root'
})

export class AuthentificationService {

  globals = Globals;

  defaultName = 'Non connecté';
  defaultPicture = 'assets/img/defaultUser.jpg';
  defaultUid = 0;
  defaultToken = '';
  defaultUuid = '';
  defaultSessid = '';
  defaultSessionname = '';

  private _currentLaravelUser: LaravelUser | null = null;

  public userSource: BehaviorSubject<User> = new BehaviorSubject({
    name: this.defaultName,
    name_label: this.defaultName,
    has_nickname: false,
    picture: this.defaultPicture,
    uid: this.defaultUid,
    token: this.defaultToken,
    uuid: this.defaultUuid,
    sessid: this.defaultSessid,
    session_name: this.defaultSessionname
  });
  user: Observable<User> = this.userSource.asObservable();

  public isLoggedInSource: BehaviorSubject<boolean> = new BehaviorSubject(false);
  isLoggedIn: Observable<boolean> = this.isLoggedInSource.asObservable();

  private initLoginSource: BehaviorSubject<boolean> = new BehaviorSubject(false);
  initLogin: Observable<boolean> = this.initLoginSource.asObservable();

  public permissionsSource: BehaviorSubject<Array<string>> = new BehaviorSubject([]);
  permissions: Observable<Array<string>> = this.permissionsSource.asObservable();

  constructor(private laravelAuthService: LaravelAuthService,
              private permissionsService: PermissionsService,
              private userSettingsService: UserSettingsService,
              private http: HttpClient) {

  }

  /**
   * Vérifie le statut de connexion au démarrage de l'application
   * Utilise Laravel Sanctum pour vérifier si la session est active
   */
  status(): Promise<any> {
    if (Globals.devBypassAuth) {
      console.warn('⚠️  devBypassAuth actif : authentification désactivée, accès admin simulé.');
      return this.test({ username: 'Dev Admin', id: 1 });
    }

    return new Promise((resolve, reject) => {
      this.laravelAuthService.checkSession()
        .then((laravelUser: LaravelUser) => {
          this._currentLaravelUser = laravelUser;
          const pictUrl: string = laravelUser.profile_picture_url || this.defaultPicture;

          this.userSource.next({
            name: laravelUser.username,
            name_label: laravelUser.nickname ? `${laravelUser.nickname} (${laravelUser.username})` : laravelUser.username,
            has_nickname: !!laravelUser.nickname,
            picture: pictUrl,
            uid: laravelUser.id,
            token: '', // Sanctum utilise des cookies, pas de token visible
            uuid: String(laravelUser.id), // On utilise l'id comme uuid pour compatibilité
            sessid: '',
            session_name: ''
          });

          this.setPermissions(true)
            .then(() => {
              this.initLoginSource.next(true);
              this.isLoggedInSource.next(true);
              // Appliquer la préférence de surnom depuis la config utilisateur
              this.applyNicknamePreferenceFromConfig();
              resolve({
                user: laravelUser,
                message: 'Session active'
              });
            });
        })
        .catch(err => {
          this.setPermissions(false)
            .then(() => {
              this.initLoginSource.next(true);
              reject(err);
            });
        });
    });
  }

  /**
   * Connexion via Laravel Sanctum
   * @param username Email de l'utilisateur (renommé pour compatibilité avec l'interface existante)
   * @param password Mot de passe
   */
  login(username: string, password: string): Promise<string> {
    return new Promise((resolve, reject) => {
      // Note: Laravel Sanctum utilise l'email pour l'authentification
      // Le paramètre username ici correspond à l'email
      this.laravelAuthService.login(username, password)
        .then((laravelUser: LaravelUser) => {
          console.log('Connexion Laravel réussie:', laravelUser);
          this._currentLaravelUser = laravelUser;

          const pictUrl: string = laravelUser.profile_picture_url || this.defaultPicture;

          this.userSource.next({
            name: laravelUser.username,
            name_label: laravelUser.nickname ? `${laravelUser.nickname} (${laravelUser.username})` : laravelUser.username,
            has_nickname: !!laravelUser.nickname,
            picture: pictUrl,
            uid: laravelUser.id,
            token: '', // Sanctum utilise des cookies
            uuid: String(laravelUser.id),
            sessid: '',
            session_name: ''
          });

          this.setPermissions(true)
            .then(() => {
              this.isLoggedInSource.next(true);
              // Appliquer la préférence de surnom depuis la config utilisateur
              this.applyNicknamePreferenceFromConfig();
              resolve('Connection réussie');
            });
        })
        .catch(err => {
          console.error('Erreur de connexion Laravel:', err);
          reject(err);
        });
    });
  }

  /**
   * Déconnexion via Laravel Sanctum
   */
  logout(): Promise<string> {
    return new Promise((resolve, reject) => {
      this.laravelAuthService.logout()
        .then(() => {
          this._currentLaravelUser = null;
          this.resetUserState();
          this.setPermissions(false)
            .then(() => {
              this.isLoggedInSource.next(false);
              resolve('Déconnexion réussie');
            });
        })
        .catch(err => {
          // En cas d'erreur, on déconnecte quand même localement
          console.error('Erreur lors de la déconnexion Laravel:', err);
          this._currentLaravelUser = null;
          this.resetUserState();
          this.setPermissions(false)
            .then(() => {
              this.isLoggedInSource.next(false);
              resolve('Déconnexion réussie');
            });
        });
    });
  }

  /**
   * Met à jour user.name selon la préférence de surnom.
   * À appeler après une modification de prefer_nickname dans les paramètres personnels.
   */
  refreshNameFromPreference(preferNickname: boolean): void {
    if (!this._currentLaravelUser) return;
    const currentUser = this.userSource.value;
    this.userSource.next({
      ...currentUser,
      name: preferNickname && this._currentLaravelUser.nickname
        ? this._currentLaravelUser.nickname
        : this._currentLaravelUser.username
    });
  }

  /**
   * Réinitialise l'état de l'utilisateur aux valeurs par défaut
   */
  private resetUserState(): void {
    this.userSource.next({
      name: this.defaultName,
      name_label: this.defaultName,
      has_nickname: false,
      picture: this.defaultPicture,
      uid: this.defaultUid,
      token: this.defaultToken,
      uuid: this.defaultUuid,
      sessid: this.defaultSessid,
      session_name: this.defaultSessionname
    });
  }

  /**
   * Récupère la config table et applique prefer_nickname sur user.name.
   * Appelé en silence après login/status — n'affecte pas le flux principal.
   */
  private applyNicknamePreferenceFromConfig(): void {
    this.userSettingsService.getConfigTable()
      .then(config => {
        if (config && config.prefer_nickname) {
          this.refreshNameFromPreference(true);
        }
      })
      .catch(() => { /* silent fail, user.name reste username par défaut */ });
  }

  setPermissions(isLogged: boolean): Promise<Array<string>> {
    return new Promise((resolve) => {
      if (isLogged) {
        this.permissionsService.readPermissions()
          .then(res => {
            const permissions = [];
            if (res) {
              for (const r of res) {
                permissions.push(r.permission);
              }
            }
            this.permissionsSource.next(permissions);
            resolve(permissions);
          })
          .catch(() => {
            this.permissionsSource.next([]);
            resolve([]);
          });
      } else {
        this.permissionsSource.next([]);
        resolve([]);
      }
    });
  }

  getUsers(): Promise<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    return new Promise((resolve, reject) => {
      this.http.get(this.globals.serverEndpoint + '/api/getUsers', {headers}).toPromise()
        .then(connection => {
          resolve(connection);
        }).catch(err => {
        reject(err);
      });
    });
  }

  /**
   * Méthode de test pour simuler une connexion (développement uniquement)
   */
  test(user): Promise<string> {
    return new Promise((resolve, reject) => {
      this.userSource.next({
        name: user.username,
        name_label: user.username,
        has_nickname: false,
        picture: this.defaultPicture,
        uid: user.id,
        token: null,
        uuid: '0',
        sessid: null,
        session_name: null
      });
      this.permissionsSource.next(['access', 'import_files', 'send_files', 'validate_files', 'view_historic', 'user_config',
        'properties_config rules_config', 'view_all_historic', 'edit_winmedia_sql',
        'edit_winmedia_preselect_monthly_playlist', 'edit_winmedia_preselect_category',
        'global_config', 'import_sons', 'edit_winmedia_spots', 'category_config']);
      this.initLoginSource.next(true);
      this.isLoggedInSource.next(true);
      resolve('OK');
    });
  }

}
